// ─────────────────────────────────────────
// main.ts — Application entry point
// Tauvior Landing Page
// ─────────────────────────────────────────

import { inject } from '@vercel/analytics';
import { loadTranslation, renderTranslation, getPageKey, getLocale, withLocaleParam } from '../i18n/index';
import { initNav }         from './nav';
import { initReveal }      from './reveal';
import { initButtons }     from './buttons';
import { initContactForm, initInterestPreset } from './contact';
import { initWhatsAppFab } from './whatsapp';
import { scrollToSection } from './utils';
import { initRouteMotion } from './route-motion';
import { initTheme }   from './theme';
import { initHeroMedia } from './heroMedia';
import { initDiagramMotion } from './diagramMotion';
import { initHomeExperience } from './homeExperience';
import { initDetailExperience } from './detailExperience';
import { initSectionRail } from './sectionRail';

/**
 * Canonical e hreflang por idioma. O idioma vem do parâmetro `?lang=` (o
 * mesmo build atende pt-BR e en-US), então cada versão canônica carrega o
 * seu próprio parâmetro e as duas se apontam como alternativas.
 */
function syncSeoLinks(): void {
  const path = window.location.pathname.replace(/\/index(\.html)?$/, '/').replace(/\.html$/, '') || '/';
  const make = (locale: string | null): string => {
    const url = new URL(path, window.location.origin);
    if (locale) url.searchParams.set('lang', locale);
    return url.toString();
  };
  document.head.querySelectorAll('link[data-seo]').forEach((el) => el.remove());
  const add = (rel: string, href: string, hreflang?: string): void => {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (hreflang) link.hreflang = hreflang;
    link.dataset.seo = '';
    document.head.appendChild(link);
  };
  const og = document.querySelector('meta[property="og:image"]');
  const ogSrc = og?.getAttribute('content');
  if (og && ogSrc && ogSrc.startsWith('/')) og.setAttribute('content', new URL(ogSrc, window.location.origin).toString());
  add('canonical', make(getLocale()));
  add('alternate', make('pt-BR'), 'pt-BR');
  add('alternate', make('en-US'), 'en-US');
  add('alternate', make(null), 'x-default');
}

function resetInitialScroll(): void {
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}

/**
 * Chegando de outra página por uma âncora (`/grid#arquitetura`, `/#contato`),
 * o topo não é o destino. O reajuste roda depois da renderização porque antes
 * dela as seções ainda não têm altura — o navegador pararia no lugar errado.
 */
function honourHashTarget(): boolean {
  const id = window.location.hash.slice(1);
  if (!id || !document.getElementById(id)) return false;

  scrollToSection({ targetId: id, behavior: 'auto' });
  return true;
}

/**
 * Âncoras que já foram da home e hoje vivem em outra página. O hash não
 * chega ao servidor, então o redirecionamento tem que ser feito aqui — sem
 * isso, link antigo e resultado de busca caem no topo da home sem explicação.
 *
 * - `#servicos` e `#metodo` viraram a página do Advisory;
 * - `#grid` (e o ainda mais antigo `#labs`) viraram a página do Grid;
 * - `#sobre` virou a página Sobre;
 * - `#ecossistema`, `#painel` e `#oee` eram das antigas páginas de
 *   aplicação, que saíram do site; o assunto delas vive hoje no Grid
 *   (`#solucoes` voltou a ser seção da home e não entra aqui);
 * - `#engineering` era a seção da Tauvior Engineering: o desenvolvimento sob
 *   medida hoje é conversado no contato da Grid; `#arquitetura` e `#verticais` foram
 *   para a página do Grid.
 */
const MOVED_FROM_HOME = new Map<string, string>([
  ['servicos',    '/advisory#formatos'],
  ['metodo',      '/advisory#metodo'],
  ['grid',        '/grid'],
  ['labs',        '/grid'],
  ['sobre',       '/sobre'],
  ['ecossistema', '/grid#arquitetura'],
  ['painel',      '/grid'],
  ['oee',         '/grid'],
  ['engineering', '/grid#contato'],
  ['arquitetura', '/grid#arquitetura'],
  ['verticais',   '/grid#aplicacoes'],
  ['tauvior-grid', '/grid'],
]);

function redirectLegacyAnchor(): boolean {
  if (getPageKey() !== 'home') return false;

  const target = MOVED_FROM_HOME.get(window.location.hash.slice(1));
  if (!target) return false;

  window.location.replace(withLocaleParam(target));
  return true;
}

async function init(): Promise<void> {
  try {
    // Antes de qualquer coisa: o script do <head> já pôs data-theme, aqui
    // entram o listener do sistema e a sincronia com o storage.
    initTheme();

    if (redirectLegacyAnchor()) return;

    resetInitialScroll();

    // Initialize Vercel Analytics
    if (import.meta.env.VITE_VERCEL_ANALYTICS === 'true') inject();

    const translation = await loadTranslation();
    renderTranslation(translation);
    syncSeoLinks();

    requestAnimationFrame(() => {
      if (!honourHashTarget()) resetInitialScroll();
      initNav();
      initReveal();
      initButtons();
      initContactForm();
      initInterestPreset();
      initWhatsAppFab();
      initRouteMotion();
      initHeroMedia();
      initDetailExperience();
      initDiagramMotion();
      initSectionRail();
      if (getPageKey() === 'home') initHomeExperience();
    });
  } catch (err) {
    console.error('[Tauvior] Failed to initialise:', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { void init(); });
} else {
  void init();
}
